/**
 * Kid Money — the full page. Two jobs off one verified identity:
 *
 *   Kids   — their own dashboard: buckets, goals, celebrations. They manage their
 *            own goals; they never see money controls or another kid's data.
 *   Adults — every child at a glance, with move / pay-out / goal controls.
 *
 * The split is a courtesy; worker/routes/kidmoney.ts is the real boundary. Every
 * endpoint derives the member from the identity, so a kid has no id to tamper with.
 *
 * The integrator wires the route (see exports at the bottom). This component is
 * prop-free so it can be dropped straight in.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { KidGoal, KidWallet, MemberSummary } from '../../shared/types';
import AdultView from './AdultView';
import KidDashboard from './KidDashboard';
import { ErrorState, LoadingState, type KidSummary } from './shared';

const EMPTY_WALLET = (memberId: number | null): KidWallet => ({
  member_id: memberId ?? 0,
  spend: 0,
  save: 0,
  give: 0,
  pending: 0,
});

export function KidMoneyPage() {
  const identity = useIdentity();
  const isAdult = identity.role === 'adult';

  const [summary, setSummary] = useState<KidSummary[] | null>(null);
  const [wallet, setWallet] = useState<KidWallet | null>(null);
  const [goals, setGoals] = useState<KidGoal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** A kid's own card comes straight off the verified identity — colour included. */
  const me = useMemo<MemberSummary | null>(
    () =>
      identity.memberId == null
        ? null
        : {
            id: identity.memberId,
            name: identity.name ?? 'Me',
            color: identity.color ?? 'var(--color-sage)',
            role: identity.role,
          },
    [identity.memberId, identity.name, identity.color, identity.role]
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      if (isAdult) {
        setSummary(await api.get<KidSummary[]>('/kidmoney/summary'));
      } else if (identity.memberId == null) {
        setWallet(EMPTY_WALLET(null));
        setGoals([]);
      } else {
        const [w, g] = await Promise.all([
          api.get<KidWallet>('/kidmoney/wallet'),
          api.get<KidGoal[]>('/kidmoney/goals'),
        ]);
        setWallet(w);
        setGoals(g);
      }
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'We could not reach the house. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [isAdult, identity.memberId]);

  useEffect(() => {
    void load();
  }, [load]);

  const retry = () => {
    setLoading(true);
    void load();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={retry} />;

  if (isAdult) {
    if (!summary) return <ErrorState message="We could not load Kid Money." onRetry={retry} />;
    return <AdultView summary={summary} reload={load} />;
  }

  if (!wallet || !goals)
    return <ErrorState message="We could not load your money." onRetry={retry} />;
  return <KidDashboard me={me} wallet={wallet} goals={goals} reload={load} />;
}

export default KidMoneyPage;
